import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonContent,
  IonText,
  IonIcon,
  IonButton,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import { backspaceOutline, lockClosedOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { verifyPin, savePin, hasPinSet } from '../services/pinUtils';
import { useAuth } from '../context/AuthContext';
import { setAuthPersisted } from '../services/pinUtils';
import { ServerService } from "../services/Server"
import './Pin.css';

var server = new ServerService();

type Mode = 'enter' | 'setup-new' | 'setup-confirm';

const PinPage: React.FC = () => {
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [mode, setMode] = useState<Mode>('enter');
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const { login, logout, isAuthenticated } = useAuth();
  const router = useIonRouter();
  const [present] = useIonToast();

  useEffect(() => {
/*    
    if(server.state.pin != "1") {
      login();
      setAuthPersisted();
      const url = server.state.ticket ? "/page/home" : "/page/settings";
      window.location.href = url;
    } else {
      logout();
    }
*/
    if (!hasPinSet()) {
      setMode('setup-new');
    }
  }, []);

  const getTitle = () => {
    if (mode === 'setup-new') return 'Create PIN';
    if (mode === 'setup-confirm') return 'Confirm PIN';
    return 'Enter PIN';
  };

  const getSubtitle = () => {
    if (mode === 'setup-new') return 'Set a 6-digit PIN to secure your app';
    if (mode === 'setup-confirm') return 'Enter your PIN again to confirm';
    return 'Enter your PIN to continue';
  };

  const currentPin = mode === 'setup-confirm' ? confirmPin : pin;
  const setCurrentPin = mode === 'setup-confirm' ? setConfirmPin : setPin;

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleDigit = async (digit: string) => {
    if (currentPin.length >= 6) return;
    const next = currentPin + digit;
    setCurrentPin(next);

    if (next.length === 6) {
      await handleComplete(next);
    }
  };

  const handleDelete = () => {
    if (currentPin.length > 0) {
      setCurrentPin(prev => prev.slice(0, -1));
    }
  };

  const handleComplete = async (fullPin: string) => {
    if (mode === 'enter') {
      const ok = await verifyPin(fullPin);
      if (ok) {
        setSuccess(true);
        setTimeout(() => {
          login();
          //router.push('/page/home', 'root', 'replace');
          setAuthPersisted();
          window.location.href = "/page/home";
        }, 600);
      } else {
        triggerShake();
        present({ message: 'Incorrect PIN. Try again.', duration: 1800, color: 'danger', position: 'top' });
        setTimeout(() => setPin(''), 400);
      }
    } else if (mode === 'setup-new') {
      setMode('setup-confirm');
    } else if (mode === 'setup-confirm') {
      if (fullPin === pin) {
        await savePin(pin);
        setSuccess(true);
        setTimeout(() => {
          login();
          //router.push('/page/home', 'root', 'replace');
          setAuthPersisted();
          window.location.href = "/page/home";
        }, 600);
      } else {
        triggerShake();
        present({ message: 'PINs do not match. Try again.', duration: 1800, color: 'danger', position: 'top' });
        setTimeout(() => {
          setPin('');
          setConfirmPin('');
          setMode('setup-new');
        }, 400);
      }
    }
  };

  const dots = Array.from({ length: 6 }, (_, i) => ({
    filled: i < currentPin.length,
    index: i,
  }));

  const numpad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['', '0', 'del'],
  ];

  return (
    <IonPage>
      <IonContent className="pin-content" fullscreen>
        <div className="pin-wrapper">
          {/* Header */}
          <div className="pin-header">
            <div className={`lock-icon-wrap ${success ? 'success' : ''}`}>
              <IonIcon
                icon={success ? checkmarkCircleOutline : lockClosedOutline}
                className="lock-icon"
              />
            </div>
            <IonText className="pin-title">{getTitle()}</IonText>
            <IonText className="pin-subtitle">{getSubtitle()}</IonText>
          </div>

          {/* Dots */}
          <div className={`pin-dots ${shake ? 'shake' : ''} ${success ? 'dots-success' : ''}`}>
            {dots.map(({ filled, index }) => (
              <div key={index} className={`dot ${filled ? 'dot-filled' : ''}`} />
            ))}
          </div>

          {/* Numpad */}
          <div className="numpad">
            {numpad.map((row, ri) => (
              <div className="numpad-row" key={ri}>
                {row.map((key, ki) => {
                  if (key === '') return <div key={ki} className="numpad-key numpad-empty" />;
                  if (key === 'del') return (
                    <button
                      key={ki}
                      className="numpad-key numpad-del"
                      onClick={handleDelete}
                      aria-label="Delete"
                    >
                      <IonIcon icon={backspaceOutline} />
                    </button>
                  );
                  return (
                    <button
                      key={ki}
                      className="numpad-key numpad-digit"
                      onClick={() => handleDigit(key)}
                    >
                      {key}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PinPage;
